import { useMemo } from "react";
import mandatorUtils from "utils/mandators";
import { useUser } from "stores/user";
import { useAllProducts } from "hooks/productHooks/useAllProducts";
import { findProductInTree } from "hooks/productHooks/utils/filterProductListByMandator";
import { useIsSameProj } from "stores/globalStore";
import { isAnyProduct, isCustomProduct } from "utils/treeUtils";
import { useCopiedItem } from "stores/project";
import type { ResultOf } from "gql.tada";
import type { ProjectListTitlesAndIds } from "api/queries";

export type ProjectListItem = NonNullable<
  NonNullable<
    ResultOf<typeof ProjectListTitlesAndIds>["currentUser"]["projectList"]
  >["items"]
>[number];

export const useFilteredProjects = (projects: ProjectListItem[]) => {
  const user = useUser();
  const copiedItem = useCopiedItem();
  const isSameProj = useIsSameProj();
  const allProducts = useAllProducts();

  const filteredProjects = useMemo(() => {
    if (!copiedItem || !isSameProj) {
      return projects;
    }
    const node = copiedItem.data;
    if (isAnyProduct(node) && !isCustomProduct(node)) {
      const productName = node?.productName;
      const seriesId = node?.orderCode?.seriesId;
      if (seriesId === undefined || productName === null) {
        return projects;
      }

      const searchedProduct = findProductInTree(
        allProducts,
        seriesId,
        productName,
      );
      const { countryOfOrigin } = searchedProduct || {};
      if (!countryOfOrigin || !user?.tenant || !projects) {
        return projects;
      }

      return mandatorUtils.filterProjectsByProductMandator(
        projects,
        countryOfOrigin,
        user?.tenant,
      );
    }
    return projects;
  }, [allProducts, user, projects, copiedItem, isSameProj]);

  return filteredProjects;
};
